/**
 * Pure display formatters for the shared components. Kept self-contained (no
 * store / app imports) so the components that use them stay store-agnostic and
 * portable.
 *
 * This is the lowest tier, so it holds only what a SHARED component needs —
 * `avatarGradient` for `AvatarChip` and `formatPrice` for the two vehicle
 * surfaces. `-mobile` re-exports both from here and keeps the formatters only
 * its own components use (`formatCountdown`, `formatSaleStart`,
 * `auctionLightName`), which is why there is no second copy of either function
 * in the monorepo.
 */

/** Ordered gradient palette used for hash-assigned avatars. */
export const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #3ee0a4, #2a8a64)',
  'linear-gradient(135deg, #ff9866, #c95a2c)',
  'linear-gradient(135deg, #6ab0ff, #2c6abf)',
  'linear-gradient(135deg, #b88dff, #6a3fc9)',
  'linear-gradient(135deg, #ffd866, #c98a2c)',
];

/**
 * Deterministically pick a gradient for a stable key (an id or a name) so the
 * same entity always gets the same color wherever it is drawn. The app used to
 * hold the only copy of this and every avatar site derived its own inline style
 * from it; `AvatarChip` now owns the derivation so the seed is all a caller
 * passes.
 *
 * @param {string | number | null | undefined} key
 * @returns {string}
 */
export const avatarGradient = (key) => {
  const text = String(key ?? '');
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) | 0;
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
};

/**
 * Whole-dollar USD with thousands separators (e.g. `$24,500`).
 * Returns `fallback` when the value is null/undefined.
 */
export const formatPrice = (
  value,
  fallback = '—'
) => (value != null ? `$${Number(value).toLocaleString('en-US')}` : fallback);

/**
 * Up-to-two-letter initials for a person's or dealership's name. Returns `·`
 * for empty input. Companion to AvatarChip: the chip owns the colour; this owns
 * the letters.
 *
 * @param {string | null | undefined} name
 * @returns {string}
 */
export const initialsOf = (name) => {
  if (!name) return '·';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '·';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
